import Vue from "vue";
import Vuex from "vuex";
import Restaurant from "@/domain/restaurant/Restaurant";
import AuthService from "@/auth/AuthService";
import User from "@/domain/user/User";
import router from "@/router";
import VuexPersistence from "vuex-persist";
import AuthDto from "@/domain/user/AuthDto";

interface State {
  selectedRestaurant: Restaurant | null;
  error: boolean;
  errorMessage: string;
  successMessage: string;
  warningMessage: string;
  loading: boolean;
  authUser: AuthDto | null;
}

const state: State = {
  selectedRestaurant: null,
  error: false,
  errorMessage: "",
  successMessage: "",
  warningMessage: "",
  loading: false,
  authUser: null
};

const authService: AuthService = new AuthService();

/* Only persists User and SelectedRestaurant */
const vuexPersist = new VuexPersistence({
  key: process.env.VUE_APP_NAME,
  storage: window.sessionStorage,
  reducer: (state: State) => ({
    authUser: state.authUser
  })
});
Vue.use(Vuex);
export default new Vuex.Store({
  plugins: [vuexPersist.plugin],
  state: state,
  mutations: {
    selectedRestaurant(state, restaurant: Restaurant) {
      state.selectedRestaurant = restaurant;
    },
    error(state, errorMessage: string) {
      state.successMessage = "";
      state.warningMessage = "";
      state.errorMessage = errorMessage;
      state.error = true;
    },
    loading(state) {
      state.loading = true;
    },
    clearError(state) {
      state.error = false;
      state.errorMessage = "";
    },
    clearLoading(state) {
      state.loading = false;
    },
    setUserInfo(state, user: AuthDto) {
      state.authUser = user;
    },
    success(state, message: string) {
      state.successMessage = message;
    },
    warning(state, message: string) {
      state.warningMessage = message;
    },
    selectFirstRestaurant(state) {
      if (state.authUser != null && state.authUser.restaurants.length > 0)
        state.selectedRestaurant = state.authUser.restaurants[0];
      else {
        state.selectedRestaurant = null;
        console.log("REST is null");
      }
    },
    addRestaurant(state, newRestaurant: Restaurant) {
      if (state.authUser != null)
        Vue.set(state.authUser, "restaurants", [
          ...state.authUser.restaurants,
          newRestaurant
        ]);
    },
    deleteRestaurant(state, restaurant: Restaurant) {
      const newList = [...state.authUser!.restaurants];
      const indexToRemove = newList.indexOf(restaurant);
      if (indexToRemove > -1) {
        newList.splice(indexToRemove, 1);
        if (state.authUser != null)
          Vue.set(state.authUser, "restaurants", newList);
      }
    }
  },
  actions: {
    selectedRestaurant({ commit }, restaurant: Restaurant) {
      commit("selectedRestaurant", restaurant);
    },
    error({ commit }, errorMessage: string) {
      commit("error", errorMessage);
    },
    loading({ commit }) {
      commit("loading");
    },
    clearError({ commit }) {
      commit("clearError");
    },
    clearLoading({ commit }) {
      commit("clearLoading");
    },
    success({ commit }, message: string) {
      commit("success", message);
    },
    clearSuccess({ commit }) {
      commit("success", "");
    },
    warning({ commit }, message: string) {
      commit("warning", message);
    },
    clearWarning({ commit }) {
      commit("warning", "");
    },
    addRestaurant({ commit }, newRestaurant: Restaurant) {
      commit("addRestaurant", newRestaurant);
    },
    deleteRestaurant({ commit }, toDelete: Restaurant) {
      commit("deleteRestaurant", toDelete);
    },
    async awsCognitoLogin({ commit }, { username }) {
        return await authService.doAwsCognitoSignIn(username)
    },
    async awsCognitoCompleteChallenge({ commit }, { password }) {
        await authService.doAwsCognitoCompleteChallenge(password)
        await this.dispatch("setUserInfo");
    },
    async logout({ commit }) {
      commit("loading");
      console.log("Logging out");
      try {
        commit("setUserInfo", null);
        commit("selectedRestaurant", null);
        commit("clearError");
        await authService.doLogout();
      } catch (error) {
        commit("error", "Invalid Login Credentials"); // TODO internationalization
      }
      if (router.currentRoute.path !== "/login") {
        console.log("pushing login");
        await router.push("/login");
      }
      commit("clearLoading");
    },
    async setUserInfo({ commit }) {
      try {
        console.log("fetching user info");
        const user = await authService.fetchUserInfo();
        if (user != null) {
          commit("setUserInfo", user);
          commit("selectFirstRestaurant");
        } else commit("error", "Invalid User Response");
      } catch (error) {
        commit("error", error.message);
        console.log("error getting User Details");
        await router.push("/");
      }
    },
    selectFirstRestaurant({ commit }) {
      commit("selectFirstRestaurant");
    }
  },
  getters: {
    isLoggedIn: () => (): Promise<boolean> => {
      return authService.isLoggedIn();
    },
    getToken: () => (): Promise<string> => {
      return authService.getIdToken();
    },
    isAdmin(state): boolean {
      return !!state.authUser && state.authUser.user.role === "ADMIN";
    },
    isManager(state, getters): boolean {
      return (
        !!state.authUser &&
        (state.authUser.user.role === "MANAGER" || getters.isAdmin)
      );
    },
    isStaff(state, getters): boolean {
      return (
        !!state.authUser &&
        (state.authUser.user.role === "STAFF" || getters.isManager)
      );
    },
    getSelectedRestaurant(state): Restaurant | null {
      return state.selectedRestaurant;
    },
    getRestaurantList(state): Restaurant[] {
      if (state.authUser != null) return state.authUser.restaurants;
      return [];
    },
    getError(state): boolean {
      return state.error;
    },
    getErrorMessage(state): string {
      return state.errorMessage;
    },
    getSuccess(state) {
      return state.successMessage;
    },
    getWarning(state) {
      return state.warningMessage;
    },
    getLoading(state): boolean {
      return state.loading;
    },
    getUser(state): User | null {
      if (state.authUser != null && state.authUser.user != null)
        return state.authUser.user;
      return null;
    },
    getLocalCountryCode(state): string {
      if (state.authUser != null && state.authUser.user != null)
        return state.authUser.user.countryCode;
      console.log("User is Null In store.getters.getLocalCountryCode");
      return "";
    }
  }
});
